/**
 * Read-only 3D thumbnail — wraps ``MeshPreview`` with an
 * IntersectionObserver gate so off-screen cart rows / admin grids
 * never allocate WebGL contexts (browsers cap at ~8–16 per page).
 *
 * Shares the configurator's renderer so what the customer sees in
 * the cart and project list is bit-for-bit the same mesh + edges
 * they configured — no second matplotlib pipeline to drift out of
 * sync.
 */
import { Suspense, lazy, useEffect, useRef, useState, type ReactNode } from "react";

import type { MeshRotation90 } from "@/components/MeshPreview";
import { meshFormatFrom } from "@/lib/meshFormat";
import { cx } from "@/lib/utils";

const MeshPreview = lazy(() => import("@/components/MeshPreview"));

interface MeshThumbnailProps {
  /** Presigned URL to the raw mesh upload. ``null``/``undefined``/empty
   * strings render the fallback (useLoader on an empty URL would
   * throw). */
  src: string | null | undefined;
  rotation: MeshRotation90;
  /** Original upload name — decides the parser (.stl/.obj/.3mf) and the
   * placeholder badge. */
  fileName?: string | null;
  color?: string;
  height?: number;
  className?: string;
  /** Rendered when ``src`` is missing. Defaults to a muted badge naming
   * the upload's own format. */
  fallback?: ReactNode;
}

export default function MeshThumbnail({
  src,
  rotation,
  fileName = null,
  color,
  height = 80,
  className,
  fallback,
}: MeshThumbnailProps): JSX.Element {
  const ref = useRef<HTMLDivElement | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (!src) return;
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setMounted(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setMounted(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin: "100px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [src]);

  if (!src) {
    return (
      <div
        ref={ref}
        className={cx("flex items-center justify-center", className)}
        style={{ height }}
      >
        {fallback ?? (
          <span className="mono rounded border border-border bg-surface-2 px-2 py-1 text-[10px] uppercase tracking-widest text-ink-3">
            {meshFormatFrom(fileName, src)}
          </span>
        )}
      </div>
    );
  }

  return (
    <div ref={ref} className={className} style={{ height }}>
      {mounted ? (
        <Suspense fallback={null}>
          <MeshPreview
            src={src}
            rotation={rotation}
            fileName={fileName}
            color={color}
            height={height}
            showGizmo={false}
          />
        </Suspense>
      ) : null}
    </div>
  );
}
